import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import api from '../services/api';

const complianceApi = {
  checkText: (text, doc_type) => api.post('/compliance/check', { text, doc_type }),
  checkFile: (fd) => api.post('/compliance/check', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

const DOC_TYPES = ['bylaws', 'rules', 'minutes', 'contract', 'policy', 'notice', 'other'];

const SEVERITY_COLORS = {
  high:   { badge: 'bg-red-100 text-red-700',    icon: '🔴', bar: 'bg-red-500' },
  medium: { badge: 'bg-yellow-100 text-yellow-700', icon: '🟡', bar: 'bg-yellow-400' },
  low:    { badge: 'bg-blue-100 text-blue-700',  icon: '🔵', bar: 'bg-blue-400' },
};

const STATUS_STYLES = {
  compliant:     { bg: 'bg-green-50 border-green-200',  text: 'text-green-700',  label: 'Compliant' },
  needs_review:  { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', label: 'Needs Review' },
  non_compliant: { bg: 'bg-red-50 border-red-200',     text: 'text-red-700',    label: 'Non-Compliant' },
};

function ScoreGauge({ score }) {
  const color = score >= 80 ? '#22c55e' : score >= 50 ? '#eab308' : '#ef4444';
  const radius = 40, cx = 50, cy = 50, strokeW = 10;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  return (
    <svg width="120" height="120" viewBox="0 0 100 100">
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeW} />
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke={color} strokeWidth={strokeW}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 50 50)" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x="50" y="54" textAnchor="middle" fontSize="20" fontWeight="bold" fill={color}>{score}</text>
      <text x="50" y="66" textAnchor="middle" fontSize="8" fill="#9ca3af">/ 100</text>
    </svg>
  );
}

export default function Compliance() {
  const [docType, setDocType] = useState('bylaws');
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const onDrop = useCallback(async (files) => {
    if (!files[0]) return;
    setChecking(true); setResult(null);
    const fd = new FormData();
    fd.append('file', files[0]);
    fd.append('doc_type', docType);
    try {
      const res = await complianceApi.checkFile(fd);
      setResult(res.data);
      toast.success('Compliance check complete');
    } catch (err) { toast.error(err.response?.data?.error || 'Check failed'); }
    finally { setChecking(false); }
  }, [docType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: false,
    accept: { 'application/pdf': [], 'text/plain': [], 'application/msword': [] },
  });

  const handleCheckText = async () => {
    if (!text.trim()) return toast.error('Paste some document text first');
    setChecking(true); setResult(null);
    try {
      const res = await complianceApi.checkText(text, docType);
      setResult(res.data);
      toast.success('Compliance check complete');
    } catch (err) { toast.error(err.response?.data?.error || 'Check failed'); }
    finally { setChecking(false); }
  };

  const highIssues   = result?.issues?.filter(i => i.severity === 'high') || [];
  const mediumIssues = result?.issues?.filter(i => i.severity === 'medium') || [];
  const lowIssues    = result?.issues?.filter(i => i.severity === 'low') || [];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">BC SPA Compliance Checker</h1>
        <p className="text-gray-500 text-sm mt-1">Upload or paste strata documents — AI checks against the BC Strata Property Act</p>
      </div>

      {/* Input section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <label className="text-sm font-medium text-gray-700">Document Type:</label>
          <select className="input w-44" value={docType} onChange={e => setDocType(e.target.value)}>
            {DOC_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </div>

        {/* Drop zone */}
        <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors mb-4 ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}>
          <input {...getInputProps()} />
          <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
          <p className="text-sm text-gray-500">Drop PDF or Word doc here, or <span className="text-blue-600">browse</span></p>
        </div>

        <div className="text-center text-gray-400 text-sm mb-4">— or paste text below —</div>

        <textarea className="input resize-none text-sm" rows={6} value={text} onChange={e => setText(e.target.value)}
          placeholder="Paste bylaw text, contract, or policy document here…" />

        <button onClick={handleCheckText} disabled={checking || !text.trim()}
          className="w-full mt-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
          {checking ? (
            <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>Checking compliance…</>
          ) : (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>Check BC SPA Compliance</>
          )}
        </button>

        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm mt-4">
          <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-amber-800">
            <span className="font-semibold">AI suggestions require your review.</span>{' '}
            Results are guidance only — always verify against the current BC Strata Property Act and consult legal counsel for binding decisions.
          </p>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Score card */}
          <div className={`rounded-xl border p-6 ${STATUS_STYLES[result.status]?.bg || 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center gap-6">
              <ScoreGauge score={result.overall_score || 0} />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-lg font-bold ${STATUS_STYLES[result.status]?.text}`}>
                    {STATUS_STYLES[result.status]?.label || result.status}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{result.summary}</p>
                <div className="flex gap-3 mt-3 text-xs">
                  <span className="text-red-600 font-medium">{highIssues.length} high</span>
                  <span className="text-yellow-600 font-medium">{mediumIssues.length} medium</span>
                  <span className="text-blue-600 font-medium">{lowIssues.length} low</span>
                  <span className="text-green-600 font-medium">{result.strengths?.length || 0} strengths</span>
                </div>
              </div>
            </div>
          </div>

          {/* Urgent actions */}
          {result.urgent_actions?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="font-semibold text-red-800 mb-2">🚨 Urgent Actions Required</h3>
              <ul className="space-y-1">
                {result.urgent_actions.map((a, i) => <li key={i} className="text-sm text-red-700 flex gap-2"><span>→</span>{a}</li>)}
              </ul>
            </div>
          )}

          {/* Issues */}
          {result.issues?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-900">Issues Found ({result.issues.length})</h3>
              </div>
              <div className="divide-y">
                {result.issues.map((issue, i) => {
                  const s = SEVERITY_COLORS[issue.severity] || SEVERITY_COLORS.low;
                  return (
                    <div key={i} className="px-5 py-4">
                      <div className="flex items-start gap-3">
                        <span className="text-lg mt-0.5">{s.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badge}`}>{issue.severity}</span>
                            {issue.section && <span className="text-xs text-gray-400">{issue.section}</span>}
                          </div>
                          <p className="text-sm font-medium text-gray-900">{issue.issue}</p>
                          {issue.recommendation && (
                            <p className="text-sm text-gray-500 mt-1">💡 {issue.recommendation}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Strengths */}
          {result.strengths?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-900">✅ What's Correct ({result.strengths.length})</h3>
              </div>
              <ul className="divide-y">
                {result.strengths.map((s, i) => (
                  <li key={i} className="px-5 py-3 text-sm text-gray-700 flex gap-2"><span className="text-green-500">✓</span>{s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <style>{`.input{width:100%;border:1px solid #d1d5db;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;outline:none}.input:focus{border-color:#3b82f6;box-shadow:0 0 0 3px rgba(59,130,246,.1)}`}</style>
    </div>
  );
}
